/**
 * CropGroups — Platform Admin страница справочника «Биологические культуры»
 * (`reference.crop_groups`, level 2 таксономии ADR-044).
 *
 * Биологическая культура — «Пшеница». Товарные под ней — «Пшеница озимая» и
 * «Пшеница яровая». Здесь живут БИОЛОГИЧЕСКИЕ свойства (латынь, семейство,
 * род, класс, шкала фенофаз, pH) — товарные их наследуют, если своё поле
 * пустое (миграция 111, просьба founder «не вбивать по 4 раза одно и то же»).
 *
 * Строка справочника (код + название + удаление) правится инлайном, биология —
 * в drawer'е (`CropGroupBioDrawer`): инлайн-редактор такие поля не тянет.
 */
import { useState } from 'react';
import { Dna } from 'lucide-react';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import { ReferenceMiniCatalog } from '@/components/reference/ReferenceMiniCatalog';
import {
  useCreateCropGroup,
  useCropGroups,
  useDeleteCropGroup,
  useUpdateCropGroup,
} from './hooks/useReferenceQueries';
import { CropGroupBioDrawer } from './CropGroupBioDrawer';
import type { CropGroup, CropGroupCreatePayload } from './types';
import { toast } from '@/lib/toast';

function plural(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return 'культур';
  if (mod10 === 1) return 'культура';
  if (mod10 >= 2 && mod10 <= 4) return 'культуры';
  return 'культур';
}

/**
 * CropGroup → payload PATCH'а. `id` / `usage_count` выкидываем: backend-схема
 * стоит на `extra="forbid"` и вернула бы 422 (AK-260 — fail-fast на дрейф имён).
 */
export function groupToPayload(g: CropGroup): CropGroupCreatePayload {
  const { id: _id, usage_count: _usage, ...rest } = g;
  return rest;
}

/** Короткая сводка биологии для колонки списка. */
export function bioSummary(g: Pick<CropGroup, 'latin_name' | 'botanical_family' | 'phenophase_scale'>): string {
  const parts = [g.latin_name, g.botanical_family, g.phenophase_scale].filter(
    (p): p is string => Boolean(p),
  );
  return parts.length ? parts.join(' · ') : '—';
}

export function PlatformAdminCropGroupsPage() {
  const q = useCropGroups();
  const createMut = useCreateCropGroup();
  const updateMut = useUpdateCropGroup();
  const deleteMut = useDeleteCropGroup();

  const [bioGroup, setBioGroup] = useState<CropGroup | null>(null);

  const items = q.data?.items ?? [];
  const byId = new Map(items.map((g) => [g.id, g]));

  return (
    <PlatformAdminLayout>
      <ReferenceMiniCatalog
        title="Биологические культуры"
        subtitle={`Пшеница, ячмень, подсолнечник · ${items.length} записей`}
        createLabel="Добавить биологическую культуру"
        usageBadgeUnit={plural}
        guardMessage="Используется товарными культурами — нельзя удалить"
        items={items}
        isLoading={q.isLoading}
        extraColumn={{
          header: 'Биология',
          width: '260px',
          render: (it) => {
            const g = byId.get(it.id);
            return g ? bioSummary(g) : '—';
          },
        }}
        rowActionSlot={(it) => (
          <button
            type="button"
            onClick={() => setBioGroup(byId.get(it.id) ?? null)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Биологические свойства"
            aria-label="Биологические свойства"
          >
            <Dna className="h-4 w-4" />
          </button>
        )}
        onCreate={async (payload) => {
          await createMut.mutateAsync(payload);
          toast.success('Биологическая культура создана');
        }}
        onUpdate={async (id, payload) => {
          // Inline правит только код и название — остальное (в т.ч. биологию)
          // сохраняем, иначе PATCH-«замена целиком» обнулила бы её.
          const current = byId.get(id);
          await updateMut.mutateAsync({
            id,
            payload: current ? { ...groupToPayload(current), ...payload } : payload,
          });
          toast.success('Биологическая культура обновлена');
        }}
        onDelete={async (id) => {
          // Отказ ловит и показывает сам ReferenceMiniCatalog (AK-574).
          await deleteMut.mutateAsync(id);
          toast.success('Биологическая культура удалена');
        }}
      />
      <CropGroupBioDrawer
        open={bioGroup !== null}
        onOpenChange={(open) => {
          if (!open) setBioGroup(null);
        }}
        group={bioGroup}
        saving={updateMut.isPending}
        onSave={async (payload) => {
          if (!bioGroup) return;
          await updateMut.mutateAsync({ id: bioGroup.id, payload });
          toast.success('Биологические свойства сохранены');
        }}
      />
      <p className="mt-3 text-[11px] text-muted-foreground">
        Товарные культуры («Пшеница озимая», «Пшеница яровая») наследуют отсюда
        латынь, ботанику, шкалу и стадии фенофаз, кислотность почвы. Заполненное
        на карточке товарной культуры всегда важнее унаследованного.
      </p>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminCropGroupsPage;
